import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  abandonFollowing,
  confirmRefresh,
  getFollowingDetail,
  listFollowings,
  previewRefresh,
  setStepProgress,
  startProject,
} from '@/api/doer'
import type { FollowingDetail } from '@/api/types'

export const FOLLOWINGS_QUERY_KEY = ['followings'] as const

/** Phase 8: keep just under the 1-hour signed-URL TTL so dashboard hero thumbs stay fresh. */
const SIGNED_URL_STALE_MS = 50 * 60 * 1000

export const useMyFollowings = () =>
  useQuery({
    queryKey: [...FOLLOWINGS_QUERY_KEY, 'list'],
    queryFn: listFollowings,
    staleTime: SIGNED_URL_STALE_MS,
  })

/**
 * Loads one Following's detail. The 404 path is meaningful — it means the user has not started
 * this project — so we don't retry on 404 and let the caller branch on {@code isError + 404}.
 */
export const useFollowingDetail = (projectId: string | undefined, enabled: boolean = true) =>
  useQuery({
    queryKey: [...FOLLOWINGS_QUERY_KEY, 'detail', projectId],
    queryFn: () => getFollowingDetail(projectId as string),
    enabled: Boolean(projectId) && enabled,
    staleTime: SIGNED_URL_STALE_MS,
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status
      if (status === 404 || status === 403) return false
      return failureCount < 3
    },
  })

export const useStartProject = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => startProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLLOWINGS_QUERY_KEY })
    },
  })
}

export const useAbandonFollowing = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => abandonFollowing(projectId),
    onSuccess: (_data, projectId) => {
      queryClient.removeQueries({ queryKey: [...FOLLOWINGS_QUERY_KEY, 'detail', projectId] })
      queryClient.invalidateQueries({ queryKey: [...FOLLOWINGS_QUERY_KEY, 'list'] })
    },
  })
}

interface ToggleProgressVars {
  projectId: string
  stepId: string
  completed: boolean
}

interface ToggleProgressCtx {
  previous: FollowingDetail | undefined
}

/**
 * Optimistic toggle: flip the local cache immediately, fall back on error. Idempotent on the
 * server too — repeating {@code completed=true} is a no-op there. Note we don't update {@code
 * completedAt} on optimistic add because we don't know the server timestamp; the refetch fixes
 * it on settle.
 */
export const useToggleStepProgress = () => {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, ToggleProgressVars, ToggleProgressCtx>({
    mutationFn: ({ projectId, stepId, completed }) => setStepProgress(projectId, stepId, completed),
    onMutate: async ({ projectId, stepId, completed }) => {
      const detailKey = [...FOLLOWINGS_QUERY_KEY, 'detail', projectId]
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<FollowingDetail>(detailKey)
      if (previous) {
        const filtered = previous.progress.filter((p) => p.snapshotStepId !== stepId)
        const next: FollowingDetail = {
          ...previous,
          progress: completed
            ? [...filtered, { snapshotStepId: stepId, completedAt: new Date().toISOString() }]
            : filtered,
        }
        queryClient.setQueryData(detailKey, next)
      }
      return { previous }
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData([...FOLLOWINGS_QUERY_KEY, 'detail', vars.projectId], ctx.previous)
      }
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({
        queryKey: [...FOLLOWINGS_QUERY_KEY, 'detail', vars.projectId],
      })
      queryClient.invalidateQueries({ queryKey: [...FOLLOWINGS_QUERY_KEY, 'list'] })
    },
  })
}

export const usePreviewRefresh = () =>
  useMutation({ mutationFn: (projectId: string) => previewRefresh(projectId) })

export const useConfirmRefresh = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => confirmRefresh(projectId),
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({
        queryKey: [...FOLLOWINGS_QUERY_KEY, 'detail', projectId],
      })
      queryClient.invalidateQueries({ queryKey: [...FOLLOWINGS_QUERY_KEY, 'list'] })
    },
  })
}
